import { create } from 'zustand';

/**
 * Storage-quota monitoring — Finding 22 of the security audit (HIGH).
 *
 * iOS Safari ships PWAs a small IndexedDB quota by default; without
 * a persistence grant the OS may evict the database in low-storage
 * conditions, silently dropping unsynced EPCRs. We:
 *   1. Call navigator.storage.persist() once on boot. The browser
 *      may grant immediately (Chrome, with PWA installed), prompt
 *      (Firefox), or silently no-op (Safari). Either way we record
 *      the result so the admin surface can show "persistent: yes/no".
 *   2. Poll navigator.storage.estimate() every minute and surface
 *      usage / quota in a zustand store the SyncStatusBanner reads.
 *   3. Trip a hard warning when usage exceeds 80% of quota so the
 *      crew can sync + free up space before a QuotaExceededError
 *      starts dropping writes.
 *
 * Browsers without the Storage API (very old Safari, in-app webview):
 *   gracefully degrade — `persistent` stays `null` and the store
 *   never updates. The app continues to work; we just lose the
 *   warning surface.
 */

interface StorageQuotaState {
  /** Whether IndexedDB is granted persistent storage (eviction-resistant). */
  persistent: boolean | null;
  /** Bytes currently used. */
  usage: number | null;
  /** Bytes of quota (typically 500MB-2GB depending on browser/install state). */
  quota: number | null;
  /** Latest fraction (0..1) — usage/quota. */
  fraction: number | null;
  /** True when fraction > 0.8 — banner-worthy. */
  warning: boolean;
  /** True when last write threw QuotaExceededError. */
  exceeded: boolean;

  refresh: () => Promise<void>;
  markExceeded: () => void;
}

export const useStorageQuotaStore = create<StorageQuotaState>((set) => ({
  persistent: null,
  usage: null,
  quota: null,
  fraction: null,
  warning: false,
  exceeded: false,

  refresh: async () => {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return;
    try {
      const est = await navigator.storage.estimate();
      const usage = est.usage ?? 0;
      const quota = est.quota ?? 0;
      const fraction = quota > 0 ? usage / quota : 0;
      set({
        usage,
        quota,
        fraction,
        warning: fraction > 0.8,
      });
    } catch {
      // ignore — estimate() is best-effort
    }
  },

  markExceeded: () => set({ exceeded: true }),
}));

/**
 * Call once on app boot. Idempotent across react-strict-mode double-
 * invocation; later calls are no-ops because `persistent` is already
 * set on the store.
 */
export async function initStorageQuota(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.storage) return;

  // Request persistence (eviction resistance). The promise resolves
  // to whether persistence was granted at the time of the call —
  // not a prompt result. On Chrome installed-PWA contexts this is
  // automatic.
  let persistent: boolean | null = null;
  try {
    if (typeof navigator.storage.persist === 'function') {
      persistent = await navigator.storage.persist();
    } else if (typeof navigator.storage.persisted === 'function') {
      persistent = await navigator.storage.persisted();
    }
  } catch {
    persistent = null;
  }
  useStorageQuotaStore.setState({ persistent });

  // Prime the initial usage estimate.
  await useStorageQuotaStore.getState().refresh();

  // Re-poll every 60s while online — IndexedDB writes happen
  // continuously during an active call, and the operator should see
  // the bar approach the warning threshold in real time.
  if (typeof window !== 'undefined') {
    window.setInterval(() => {
      void useStorageQuotaStore.getState().refresh();
    }, 60_000);
  }
}

/**
 * True when an error message indicates a quota violation. Used by
 * the sync layer to flip the global exceeded flag — the next attempt
 * surfaces a banner rather than silently retrying.
 */
export function isQuotaError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return /QuotaExceededError|quota|storage.*full/i.test(msg)
    || (err instanceof DOMException && err.name === 'QuotaExceededError');
}
